'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import styles from './dental.module.css';

const steps = [
  {
    num: '01',
    title: 'Share Your Practice Details',
    desc: 'Upload your services, hours, and location — or drop in your website URL and we extract everything automatically.',
    illus: (
      <div className={styles.illusStack}>
        <div className={`${styles.illusDoc} ${styles.illusDocBack2}`}>
          <span>yourdentalpractice.com</span>
        </div>
        <div className={`${styles.illusDoc} ${styles.illusDocBack1}`}>
          <span>Office Hours</span>
        </div>
        <div className={`${styles.illusDoc} ${styles.illusDocFront}`}>
          <span className={styles.illusDocIcon}>📋</span>
          <span>Services.pdf</span>
        </div>
        <div className={styles.illusAgent}>
          <span>🤖</span>
          <span>Dental Agent</span>
        </div>
      </div>
    ),
  },
  {
    num: '02',
    title: 'Train Your Agent',
    desc: 'Name your agent, customize its greeting and tone, connect your dental software (Dentrix, Eaglesoft, Open Dental), and set language preferences.',
    illus: (
      <div className={styles.illusPanel}>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Agent Name</span>
          <span className={styles.illusPanelValue}>Denta</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Tone</span>
          <span className={styles.illusPanelValue}>Warm &amp; Professional</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Software</span>
          <span className={styles.illusPanelValue}>Dentrix · Eaglesoft</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Appointments</span>
          <span className={`${styles.illusPanelValue} ${styles.illusPanelOn}`}>Enabled ✓</span>
        </div>
      </div>
    ),
  },
  {
    num: '03',
    title: 'Claim Your Phone Number',
    desc: 'Choose a dedicated local number for your dental practice. Forward your existing number or start using the new one immediately.',
    illus: (
      <div className={styles.illusPhone}>
        <div className={styles.illusPhoneLabel}>Your Local Number</div>
        <div className={styles.illusPhoneNum}>(555) 428-7700</div>
        <div className={styles.illusPhoneBadge}>✓ Ready to receive calls</div>
      </div>
    ),
  },
  {
    num: '04',
    title: 'Go Live 24/7',
    desc: 'Your dental agent is on. Every patient call answered, every appointment captured — no hold times, no missed calls.',
    illus: (
      <div className={styles.illusLive}>
        <div className={styles.illusLiveBadge}>
          <span className={styles.illusLiveDot} />
          LIVE
        </div>
        <ul className={styles.illusLiveList}>
          <li>No hold times</li>
          <li>No missed appointments</li>
          <li>No extra staff needed</li>
        </ul>
      </div>
    ),
  },
];

export default function DentalAgentPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const goTo = (idx: number) => {
    const clamped = Math.max(0, Math.min(idx, steps.length - 1));
    setCurrentSlide(clamped);
    const vp = viewportRef.current;
    if (!vp) return;
    const cards = vp.querySelectorAll('[data-card]');
    cards[clamped]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  };

  return (
    <div className={styles.page}>

      {/* ── Navigation ── */}
      <NavBar />

      {/* ── Hero ── */}
      <section className={styles.hero}>
        <Image
          src="/images/dental.png"
          alt="Dental background"
          fill
          priority
          className={styles.heroImage}
        />
        <div className={styles.heroOverlay} />
        <div className={styles.heroLayout}>
          <div className={styles.heroContent}>
            <span className={styles.heroTag}>Dental AI Voice Agent</span>
            <h1 className={styles.heroTitle}>Your Front Desk. Reimagined.</h1>
            <p className={styles.heroSubtitle}>
              Your AI agent books appointments, answers patient questions, handles insurance inquiries, and verifies patient identity — around the clock, no exceptions.
            </p>
            <div className={styles.heroBtns}>
              <Link href="/register?industry=dental" className={styles.heroBtnPrimary}>Get Started</Link>
              <Link href="#how-it-works" className={styles.heroBtnSecondary}>See How It Works</Link>
            </div>
          </div>

          {/* ── Live Demo Widget ── */}
          <div className={styles.audioWidget}>
            <div className={styles.audioWidgetHeader}>
              <div className={styles.audioAvatar}>AI</div>
              <div>
                <div className={styles.audioAgentName} style={{ marginLeft: '44px' }}>Nova</div>
                <div className={styles.audioAgentStatus}>
                  <span className={styles.audioStatusDotActive} />
                  Live · Dental AI Agent
                </div>
              </div>
            </div>
            <div className={styles.audioWaveform}>
              {[...Array(16)].map((_, i) => (
                <span key={i} className={`${styles.audioWaveBar} ${styles.audioWaveBarActive}`} style={{ animationDelay: `${i * 0.07}s` }} />
              ))}
            </div>
            <a href="tel:5599109756" className={styles.audioPlayBtn} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
              </svg>
              559-910-9756
            </a>
            <p className={styles.audioLabel}>Tap to call · Available 24/7</p>
          </div>

        </div>
      </section>

      {/* ── Features ── */}
      <section className={styles.featuresSection}>
        <div className={styles.featuresInner}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>HIPAA COMPLIANT · ALWAYS ON · INSTANTLY INTELLIGENT</span>
            <h2>Every Patient Call Answered.<br />Every Appointment Booked.<br />Every Time.</h2>
            <p>NovaMind AI handles your phone lines so your front desk doesn&apos;t have to — booking appointments, answering patient questions, and handling insurance inquiries 24/7 with zero hold times.</p>
          </div>
          <div className={styles.featuresGrid}>

            {/* Card 1 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusCallWrap}>
                  <div className={styles.illusCallBadge}>
                    <span className={styles.illusCallDot} /> Live · Incoming Call
                  </div>
                  <div className={styles.illusCallNumber}>(555) 428-7700</div>
                  <div className={styles.illusWaveBars}>
                    {[...Array(12)].map((_, i) => (
                      <span key={i} className={styles.illusWaveBar} style={{ animationDelay: `${i * 0.08}s` }} />
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Instant Call Answering & Routing</h3>
                <p className={styles.fcSubtitle}>AI answers 100% of inbound calls and routes to the right person with zero hold time.</p>
              </div>
            </div>

            {/* Card 2 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusApptCard}>
                  <div className={styles.illusApptTitle}>New Appointment</div>
                  <div className={styles.illusApptRow}><span>Patient</span><span>Maria Lopez</span></div>
                  <div className={styles.illusApptRow}><span>Date</span><span>Tue, Apr 8</span></div>
                  <div className={styles.illusApptRow}><span>Time</span><span>2:00 PM</span></div>
                  <div className={styles.illusApptRow}><span>Provider</span><span>Dr. Kim</span></div>
                  <div className={styles.illusApptConfirm}>✓ Confirmed</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Appointment Scheduling</h3>
                <p className={styles.fcSubtitle}>Books, confirms, reschedules, and cancels appointments automatically — 24/7.</p>
              </div>
            </div>

            {/* Card 3 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusVerifyCard}>
                  <div className={styles.illusVerifyTitle}>Patient Verification</div>
                  <div className={styles.illusVerifyRow}><span>Name</span><span>David Chen</span></div>
                  <div className={styles.illusVerifyRow}><span>DOB</span><span>07 / 22 / 1990</span></div>
                  <div className={styles.illusVerifyRow}><span>Member ID</span><span>DEN-445821</span></div>
                  <div className={styles.illusVerifyBadge}>✓ Identity Verified</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Patient Identity Verification</h3>
                <p className={styles.fcSubtitle}>Securely verifies patient identity before connecting to staff or sharing any information.</p>
              </div>
            </div>

            {/* Card 4 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusInsCard}>
                  <div className={styles.illusInsTitle}>Insurance Inquiry</div>
                  <div className={styles.illusInsRow}><span>Provider</span><span>Delta Dental</span></div>
                  <div className={styles.illusInsRow}><span>Plan</span><span>PPO Plus</span></div>
                  <div className={styles.illusInsRow}><span>Annual Max</span><span>$2,000</span></div>
                  <div className={styles.illusInsRow}><span>Remaining</span><span>$1,450</span></div>
                  <div className={styles.illusInsBadge}>✓ Coverage Confirmed</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Insurance Inquiries</h3>
                <p className={styles.fcSubtitle}>Answers patient questions about coverage, copays, annual maximums, and in-network status.</p>
              </div>
            </div>

            {/* Card 5 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusTreatCard}>
                  <div className={styles.illusTreatTitle}>Treatment Plan Q&amp;A</div>
                  <div className={styles.illusTreatRow}><span>Procedure</span><span>Crown</span></div>
                  <div className={styles.illusTreatRow}><span>Est. Cost</span><span>$1,200</span></div>
                  <div className={styles.illusTreatRow}><span>Insurance</span><span>Covers 50%</span></div>
                  <div className={styles.illusTreatRow}><span>Out-of-Pocket</span><span>~$600</span></div>
                  <div className={styles.illusTreatBadge}>✓ Info Provided</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Treatment Plan Questions</h3>
                <p className={styles.fcSubtitle}>Answers patient questions about upcoming procedures, costs, and what to expect.</p>
              </div>
            </div>

            {/* Card 6 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusLangWrap}>
                  {[
                    { lang: 'EN', text: 'I need to book a cleaning.' },
                    { lang: 'ES', text: 'Necesito reservar una limpieza.' },
                    { lang: 'ZH', text: '我需要预约洗牙。' },
                  ].map((b, i) => (
                    <div key={i} className={styles.illusLangBubble}>
                      <span className={styles.illusLangTag}>{b.lang}</span>
                      <span className={styles.illusLangText}>{b.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Multi-language on Request</h3>
                <p className={styles.fcSubtitle}>Configured for the languages your patients speak — add support based on your community&apos;s needs.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className={styles.howItWorksSection}>
        <div className={styles.howItWorksHeader}>
          <div>
            <h2 className={styles.howTitle}>
              <em className={styles.howTitleItalic}>How</em> It Works.
            </h2>
            <p className={styles.howSubtitle}>From sign-up to live patient calls in under 5 minutes</p>
          </div>
          <div className={styles.sliderArrows}>
            <button
              className={styles.arrowBtn}
              onClick={() => goTo(currentSlide - 1)}
              disabled={currentSlide === 0}
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              className={styles.arrowBtn}
              onClick={() => goTo(currentSlide + 1)}
              disabled={currentSlide === steps.length - 1}
              aria-label="Next"
            >
              ›
            </button>
          </div>
        </div>

        <div className={styles.sliderViewport} ref={viewportRef}>
          {steps.map((step, i) => (
            <div
              key={i}
              data-card
              className={`${styles.slideCard} ${styles[`slideCard${i + 1}`]}`}
            >
              <span className={styles.slideNum}>{step.num}</span>
              <div className={styles.slideIllus}>{step.illus}</div>
              <div className={styles.slideText}>
                <h3 className={styles.slideTitle}>{step.title}</h3>
                <p className={styles.slideDesc}>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className={styles.ctaSection}>
        <Image
          src="/images/nurse.png"
          alt="CTA background"
          fill
          priority
          className={styles.ctaImage}
        />
        <div className={styles.ctaOverlay} />
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>Ready to Transform Your Dental Practice?</h2>
          <p className={styles.ctaSubtitle}>Every patient greeted with warmth and professionalism — appointment scheduling, insurance questions, and more. 24/7.</p>
          <Link href="/register?industry=dental" className={styles.ctaBtn}>Get Started</Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <Footer />

    </div>
  );
}
